import {
  BotDifficulty,
  LobbyVisibility as WireVisibility,
  Team,
} from '../protocol/gen/warinspace/v2/common_pb.js';
import type {
  LobbyListing,
  LobbyParticipant as WireParticipant,
  LobbySeat,
  LobbyState,
  ProfileShip as WireProfileShip,
} from '../protocol/gen/warinspace/v2/server_to_client_pb.js';
import type { ProfileShip } from '../account/profile-ship';
import { teamFromWire } from '../game/world';
import {
  DEFAULT_PLACEHOLDER_DIFFICULTY,
  NO_PARTICIPANT,
  TEAMS,
  type LobbyListRow,
  type LobbyParticipant,
  type LobbySlot,
  type LobbyTeam,
  type LobbyView,
  type LobbyVisibility,
  type PlaceholderDifficulty,
} from './lobby-state';

export function difficultyFromWire(difficulty: BotDifficulty): PlaceholderDifficulty {
  switch (difficulty) {
    case BotDifficulty.RECRUIT:
      return 'Recruit';
    case BotDifficulty.VETERAN:
      return 'Veteran';
    case BotDifficulty.ELITE:
      return 'Elite';
    case BotDifficulty.REGULAR:
    case BotDifficulty.UNSPECIFIED:
      return DEFAULT_PLACEHOLDER_DIFFICULTY;
  }
}

export function difficultyToWire(difficulty: PlaceholderDifficulty): BotDifficulty {
  switch (difficulty) {
    case 'Recruit':
      return BotDifficulty.RECRUIT;
    case 'Regular':
      return BotDifficulty.REGULAR;
    case 'Veteran':
      return BotDifficulty.VETERAN;
    case 'Elite':
      return BotDifficulty.ELITE;
  }
}

export function visibilityFromWire(visibility: WireVisibility): LobbyVisibility {
  return visibility === WireVisibility.PRIVATE ? 'private' : 'public';
}

export function visibilityToWire(visibility: LobbyVisibility): WireVisibility {
  return visibility === 'private' ? WireVisibility.PRIVATE : WireVisibility.PUBLIC;
}

export function teamToWire(team: number): Team {
  return team === 1 ? Team.RED : Team.BLUE;
}

function teamOf(team: Team): number {
  return teamFromWire(team) === 1 ? 1 : 0;
}

export function profileShipFromWire(wire: WireProfileShip | undefined): ProfileShip | null {
  if (wire === undefined) return null;
  return {
    shipId: wire.shipId,
    skinProductId: wire.skinProductId,
    decalProductId: wire.decalProductId,
    trailProductId: wire.trailProductId,
    tagProductId: wire.tagProductId,
  };
}

function participantFrom(wire: WireParticipant): LobbyParticipant {
  return {
    participantId: wire.participantId,
    name: wire.name,
    guest: wire.guest,
    profileShip: profileShipFromWire(wire.profileShip),
  };
}

function slotFrom(seat: LobbySeat): LobbySlot {
  const ship = { shipId: seat.shipId, skinProductId: seat.skinProductId };
  const team = teamOf(seat.team);
  if (seat.participantId === NO_PARTICIPANT) {
    return { slotId: seat.slotId, team, occupant: { kind: 'bot' }, ship };
  }
  return {
    slotId: seat.slotId,
    team,
    occupant: {
      kind: 'player',
      player: {
        participantId: seat.participantId,
        name: seat.name,
        guest: seat.guest,
        profileShip: profileShipFromWire(seat.profileShip),
      },
    },
    ship,
  };
}

export function lobbyViewFrom(state: LobbyState): LobbyView {
  const teams: LobbyTeam[] = TEAMS.map((team) => ({
    team,
    difficulty: DEFAULT_PLACEHOLDER_DIFFICULTY,
  }));
  for (const entry of state.allTeams) {
    const team = teamOf(entry.team);
    teams[team] = { team, difficulty: difficultyFromWire(entry.difficulty) };
  }

  return {
    joinCode: state.joinCode,
    name: state.name,
    hostName: state.hostName,
    visibility: visibilityFromWire(state.visibility),
    teams,
    slots: state.allSeats.map(slotFrom).sort((left, right) => left.slotId - right.slotId),
    spectators: state.allSpectators.map(participantFrom),
    hostParticipantId: state.hostParticipantId,
    started: state.started,
    startInMs: state.startInMs,
  };
}

export function lobbyRowFrom(listing: LobbyListing): LobbyListRow {
  return {
    joinCode: listing.joinCode,
    name: listing.name,
    hostName: listing.hostName,
    hostProfileShip: profileShipFromWire(listing.hostProfileShip),
    difficulties: listing.allDifficulties.map(difficultyFromWire),
    humans: listing.humans,
    capacity: listing.capacity,
    spectators: listing.spectators,
  };
}

export function lobbyRowsFrom(listings: readonly LobbyListing[]): readonly LobbyListRow[] {
  return listings.map(lobbyRowFrom);
}
